// GPS route over the Eixample grid. Pure, so it can be tested without WebGL.

// A walker is ~.92 u tall, so one world unit is ~1.9 m.
const M_PER_U = 1.9;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// Nearest point on any street: streets are full lines x = s or z = s spanning the grid.
function snap([x, z], streets) {
  const lo = Math.min(...streets),
    hi = Math.max(...streets);
  let best = null;
  for (const s of streets)
    for (const c of [
      { p: [s, clamp(z, lo, hi)], v: true, s },
      { p: [clamp(x, lo, hi), s], v: false, s },
    ]) {
      c.d = dist([x, z], c.p);
      if (!best || c.d < best.d) best = c;
    }
  return best;
}

// The shortest way from p to q along the streets, as corner points from p to q. Every street crosses every other
// one, so a route needs at most one cross street between two parallel ones. Snap legs shorter than `near` are
// dropped, so riding in a lane the guide follows the street instead of pointing at its centre line.
export function streetRoute(p, q, streets, near = 6) {
  if (dist(p, q) < near * 2) return { pts: [p, q], len: dist(p, q) };
  const A = snap(p, streets),
    B = snap(q, streets);
  let mid = [];
  if (A.v === B.v && A.s !== B.s) {
    let bestT = streets[0],
      cost = Infinity;
    for (const t of streets) {
      const c = A.v
        ? Math.abs(A.p[1] - t) + Math.abs(B.p[1] - t)
        : Math.abs(A.p[0] - t) + Math.abs(B.p[0] - t);
      if (c < cost) [cost, bestT] = [c, t];
    }
    mid = A.v
      ? [
          [A.p[0], bestT],
          [B.p[0], bestT],
        ]
      : [
          [bestT, A.p[1]],
          [bestT, B.p[1]],
        ];
  } else if (A.v !== B.v) mid = [A.v ? [A.p[0], B.p[1]] : [B.p[0], A.p[1]]];
  const raw = [p, ...(A.d < near ? [] : [A.p]), ...mid, ...(B.d < near ? [] : [B.p]), q];
  const pts = raw.filter((c, i) => i === 0 || dist(c, raw[i - 1]) > 0.01);
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += dist(pts[i - 1], pts[i]);
  return { pts, len };
}

// Yaw to show (atan2(dx, dz), like the scooter's): along the current leg, or into the next one once its corner is
// within `turnAt`.
export function guideHeading(pts, turnAt) {
  const [a, b] =
    pts.length > 2 && dist(pts[0], pts[1]) < turnAt ? [pts[1], pts[2]] : [pts[0], pts[1]];
  return Math.atan2(b[0] - a[0], b[1] - a[1]);
}

export const routeMetres = (len) => Math.max(10, Math.round((len * M_PER_U) / 10) * 10);
